import type { AxiosInstance, AxiosError } from "axios";
import { useAuth } from "@/shared/stores/auth";
import router from "@/shared/services/router";

const TOKEN_TYPE = "Bearer";
const REFRESH_TOKEN_URL = "/auth/refresh";
const LOGIN_URL = "/auth/login";
const UNAUTHORIZED_CODE = 401;
const BAD_GATEWAY_CODE = 502;

let isRefreshing = false;
let failedRequestQueue: {
  onSuccess: (token: string) => void;
  onFailure: (error: AxiosError) => void;
}[] = [];

export function setupResponseInterceptor(api: AxiosInstance) {
  api.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (!error.response || !error.config) {
        return Promise.reject(error);
      }

      const originalConfig = error.config;
      const authStore = useAuth();

      if (error.response.status === UNAUTHORIZED_CODE) {
        if (originalConfig.url === REFRESH_TOKEN_URL) {
          authStore.clearAuth();
          router.push(LOGIN_URL);
          return Promise.reject(error);
        }

        if (!isRefreshing) {
          isRefreshing = true;

          api
            .post(REFRESH_TOKEN_URL, {
              refreshToken: authStore.refreshToken,
            })
            .then((response) => {
              const { accessToken, refreshToken: newRefreshToken } = response.data;

              authStore.setAccessToken(accessToken);
              authStore.setRefreshToken(newRefreshToken);

              api.defaults.headers.common["Authorization"] = `${TOKEN_TYPE} ${accessToken}`;

              failedRequestQueue.forEach((request) => request.onSuccess(accessToken));
              failedRequestQueue = [];
            })
            .catch((err) => {
              failedRequestQueue.forEach((request) => request.onFailure(err));
              failedRequestQueue = [];

              authStore.clearAuth();
              router.push(LOGIN_URL);
            })
            .finally(() => {
              isRefreshing = false;
            });
        }

        return new Promise((resolve, reject) => {
          failedRequestQueue.push({
            onSuccess: (accessToken: string) => {
              if (originalConfig.headers) {
                originalConfig.headers["Authorization"] = `${TOKEN_TYPE} ${accessToken}`;
              }

              resolve(api(originalConfig));
            },
            onFailure: (err: AxiosError) => {
              reject(err);
            },
          });
        });
      } else if (error.response.status === BAD_GATEWAY_CODE) {
        router.push(LOGIN_URL);
      }

      return Promise.reject(error);
    },
  );
}
